import type {StyleInfo} from "lit/directives/style-map.js";

import {buildHeaderItemStyleMap} from "../../../helpers/header-item-style.helper";

/** How many places the picker's fallback palette has (`--gimmick-palette-1…8` in ralphy.css). */
export const FALLBACK_PALETTE_SIZE = 8;

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
const WHITE = new Set(['#fff', '#ffffff']);

/**
 * The custom properties a wheel segment, drum ball or the turned card carries on its `<g>` (feature plan 0072): `--o2d-color-1`
 * for the face, `--text-color` for the shortcut on it; ralphy.css fills with them.
 *
 * The employee's own colour only when it is a valid hex (`#rgb`/`#rrggbb`) and not white — white is every
 * entity's default and would vanish on the light face; only valid hex values reach `buildHeaderItemStyleMap`,
 * which warns for anything else. Otherwise a place of the fallback palette, stable per uid.
 */
export function poolColorStyle(uid: string, color: string, textColor: string | null = null): StyleInfo {
    const value = color.trim();
    if (HEX_COLOR.test(value) && !WHITE.has(value.toLowerCase())) {
        // The employee's own text colour, if set (feature plan 0074); the fallback palette keeps its paired tokens.
        return buildHeaderItemStyleMap(value, textColor);
    }
    const place = fallbackPlace(uid);
    return {'--o2d-color-1': `var(--gimmick-palette-${place})`, '--text-color': `var(--gimmick-palette-text-${place})`};
}

/** 1…8, from a string hash of the uid — the same employee keeps their place when others come or go. */
export function fallbackPlace(uid: string): number {
    let hash = 0;
    for (let i = 0; i < uid.length; i++) {
        hash = (Math.imul(hash, 31) + uid.charCodeAt(i)) >>> 0;
    }
    return (hash % FALLBACK_PALETTE_SIZE) + 1;
}
